<template>
  <div>
    <div class="bg-grey-lightest pb-2 lg:pb-6">
      <div class="grid-wrapper">
        <div class="xl:col-10 m-auto">
          <PageHeading
            :search-term="pageHeadingSearchTerm"
            :page="cmsPage"
            :selected-collection="selectedCollection"
            :results-count="totalResults"
            :error="error"
            :categories="categories"
          />
          <Search :search-term="searchTerm" />
          <CollectionOptions
            :collection-options="collectionOptions"
            :selected-collection="selectedCollection"
          />
        </div>
      </div>
    </div>
    <LibrisFilter
      left-title="scale"
      :selected-filters="selectedFilters"
      :left-options="scaleOptions"
      :left-options-interpolation="leftOptionsInterpolation()"
      :right-options="colorOptions"
      :count="totalResults"
      :loading="loadingData"
      class="my-6"
    />
    <LibrisModal
      v-if="activeImage"
      ref="librisModal"
      :modal-open="imageModalOpen"
      :link="activeImage.includes.link.data.attributes.link"
      :custom-metadata="
        activeImage.includes['custom-metadata'].data.attributes.custom_metadata
      "
      :display-name="activeImage.includes.iptc.data.attributes.description"
      :tags="activeImage.includes.iptc.data.attributes.keywords"
      :searchTerm="searchTerm"
    />
    <div class="grid-wrapper">
      <div class="xl:col-10 m-auto">
        <MasonryGrid
          v-if="!loadingData"
          :search-results="results"
          :error="error"
          :api-call-made="apiCallMade"
          :current-page="Number(page)"
          :total-results="totalResults"
          :per-page="24"
          :no-results-message="cmsPage.noResults"
          :no-results-redirect="cmsPage.noResultsSlug"
          :total-pages="totalPages"
        />
        <div class="libris-search-loading-container">
          <div v-if="loadingData" class="loading"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import queryString from 'query-string';
import axios from 'axios';
import MicroModal from 'micromodal';
import find from 'lodash.find';
import debounce from 'lodash/debounce';

import {
  objectHasKeysDefined,
  htmlEntities,
  shuffle,
} from '../../helpers/helpers';
import { fireAdobeAnalyticsEvent } from '../../helpers/adobe-analytics';
import {
  LIBRIS_COLOR_OPTIONS,
  LIBRIS_CATEGORY_ID,
  LIBRIS_COLLECTION_OPTIONS,
} from '../../constants';
import LibrisFilter from './filter/LibrisFilter.vue';
import LibrisModal from './modal/LibrisModal.vue';
import Search from './Search.vue';
import CollectionOptions from './CollectionOptions';
import PageHeading from './PageHeading';
import MasonryGrid from './MasonryGrid';

import {
  eventBus,
  CLEAR_FILTERS,
  UPDATE_FILTERS,
  UPDATE_SELECTED_COLLECTION,
  UPDATE_SEARCH_TERM,
  UPDATE_ACTIVE_IMAGE_INDEX,
  UPDATE_CURRENT_PAGE,
  REFRESH_SEARCH,
} from '../shared/event-bus';

const gallery_id = 'G00008M7m7Bg4ZX4';
const org_id = 'O0000De6VE5p4rwg';
const per_page = 24;

// the various string keys that we need to send to Libris SEACH API
const queryKeys = {
  QUERY_SEARCH_TERM: 'searchTerm',
  QUERY_SELECTED_COLLECTION: 'selectedCollection',
  QUERY_FILTER_SCALE: 'filterScale',
  QUERY_FILTER_COLOR: 'filterColor',
  QUERY_CURRENT_PAGE: 'page',
};

// a mapping of the query strings to the state object properties
const queryToStateMap = {
  [queryKeys.QUERY_SEARCH_TERM]: 'searchTerm',
  [queryKeys.QUERY_SELECTED_COLLECTION]: 'selectedCollection',
  [queryKeys.QUERY_FILTER_SCALE]: `selectedFilters.scale`,
  [queryKeys.QUERY_FILTER_COLOR]: `selectedFilters.color`,
  [queryKeys.QUERY_CURRENT_PAGE]: 'page',
};

// strip out the string mainpulation for the url into a function
// if you are appending to the a url that already has a query string '?'
// then do one string manipulation
// otherise to just add
const newUrlCreator = appendMode => (stringAcc, obj, val) => {
  const acc = stringAcc;
  if (
    ((val == 'searchTerm' || val == 'image') && obj[val] != '') ||
    val == 'page'
  ) {
    stringAcc = appendMode
      ? `${stringAcc}&${val}=${obj[val]}`
      : `?${val}=${obj[val]}`;
  } else if (val == 'selectedCollection' && obj[val].option) {
    stringAcc = appendMode
      ? `${stringAcc}&${val}=${obj[val].option}`
      : `?${val}=${obj[val].option}`;
  } else {
    const valToUse = obj[val] ? obj[val].option : '';
    stringAcc = valToUse
      ? appendMode
        ? `${stringAcc}&${val}=${valToUse}`
        : `?${val}=${valToUse}`
      : stringAcc;
  }

  return stringAcc;
};

// Libris only indexes some images by bare number, so OR in the IM-prefixed
// ID for any 5-digit token ("12837", "IM12837", "IM-12837" or "#12837")
const withImageIdPrefix = term =>
  term.replace(
    /(^|\s)(?:3form[-\s]*)?(?:im)?[-\s#]*(\d{5})(?:\.\w+)?(?=\s|$)/gi,
    '$1$2 IM$2'
  );

// Visitors never have to type the prefix: "12837", "IM 12837", "#12837" and
// a downloaded "3FORM-IM12837.jpg" all mean IM12837
const normalizeImageId = value => {
  const match = /^(?:3FORM[-\s]*)?(?:IM)?[-\s#]*(\d{5})(?:\.\w+)?$/i.exec(
    String(value || '').trim()
  );
  return match ? `IM${match[1]}` : '';
};

const imageIdOf = image => {
  const match = /IM(\d{5})(?!\d)/i.exec(image.attributes.file_name || '');
  return match ? `IM${match[1]}` : '';
};

export default {
  name: 'LibrisSearch',
  components: {
    PageHeading,
    Search,
    CollectionOptions,
    LibrisFilter,
    LibrisModal,
    MasonryGrid,
  },
  // these are props that we receive from the ruby template
  props: ['categories', 'coolPage'],
  // You can think of data as the 'state' function for the whole app
  // it is requried to return a JS Object and any changes to the state happen via accessing a key on the 'this' context
  // this.searchTerm = newVal
  data() {
    return {
      searchTerm: '',
      pageHeadingSearchTerm: '',
      loadingData: true,
      imageModalOpen: false,
      selectedCollection: { option: '', field_id: '', option_id: '' },
      collectionOptions: LIBRIS_COLLECTION_OPTIONS,
      activeImage: null,
      openImageId: '',
      selectedFilters: {
        color: {
          field_id: '',
          option_id: '',
          option: '',
        },
        scale: {
          field_id: '',
          option_id: '',
          option: '',
        },
      },
      scaleOptions: [
        {
          field_id: 'Scale_fc746db83178b4d8',
          option_id: 'mural_f67744e640fe0d0a',
          option: 'mural',
        },
      ],
      // the different color options for the color metadata field
      colorOptions: LIBRIS_COLOR_OPTIONS,
      // results returned from the search query
      results: [],
      page: 1,
      totalResults: 0,
      totalPages: null,
      modalReady: false,
      error: null,
      apiCallMade: false,
    };
  },
  // computed properties are properits that require some sort of business logic and will only update if one of their dependencies change
  // DO NOT TRY TO USE THESE LIKE REGULAR FUNCTIONS -- THEY NEED TO BE USED ONLY IN THE TEMPLATE SECTION.
  computed: {
    cmsPage() {
      if (this.coolPage) {
        return JSON.parse(this.coolPage);
      }

      return {
        title: 'Default title -- expected JSON',
        subheading: 'Default subheading',
        noResults: 'Default no results',
      };
    },
  },
  async mounted() {
    window.onpopstate = async () => {
      await this.handleSearchTerms();
      await this.makeSearchRequest();
      await this.openImageFromUrl();
    };
    /* await this.getCategories(); */
    await this.handleSearchTerms();
    await this.makeSearchRequest();
    await this.openImageFromUrl({ sharedLink: true });
    if (this.searchTerm) {
      fireAdobeAnalyticsEvent({
        type: 'tool',
        detail: {
          eVar35: `Image Gallery|Search|${this.searchTerm}`,
          search: {
            term: this.searchTerm,
            results: !!this.results.length,
            toolName: 'Image Gallery',
          },
        },
      });
    }
    /**
     * Vue works with the concept of Props down and Events up.
     * Rather than set up a bunch of event listernes and listening all the way
     * through the component tree it is a pub/sub system that allows us to emit
     * events and have components change based on the events they subscrib to.
     * read more here: https://alligator.io/vuejs/global-event-bus/
     */
    eventBus.$on(CLEAR_FILTERS, () => {
      this.selectedFilters = {
        color: { field_id: '', option_id: '', option: '' },
        scale: { field_id: '', option_id: '', option: '' },
      };
      this.page = '1';
      this.updatedUpdateUrl();
    });

    eventBus.$on(UPDATE_FILTERS, data => {
      this.selectedFilters = { ...this.selectedFilters, ...data };
      this.page = '1';
      this.updatedUpdateUrl();
    });

    eventBus.$on(UPDATE_SELECTED_COLLECTION, data => {
      this.selectedCollection = { ...data };
      this.page = '1';
      this.updatedUpdateUrl();
    });

    eventBus.$on(UPDATE_SEARCH_TERM, data => {
      if (data.length >= 3 || data.length === 0) {
        this.searchTerm = data;
        this.page = '1';
        this.updatedUpdateUrl();
        this.sendAdobeSearchEvent(data);
      }
    });
    eventBus.$on(UPDATE_ACTIVE_IMAGE_INDEX, data => {
      this.openImage(this.results[data]);
      try {
        this.sendAdobeResultClickEvent(data);
        this.sendAdobeModalOpenEvent(data);
      } catch (err) {
        console.error('Libris analytics error', err);
      }
    });
    eventBus.$on(UPDATE_CURRENT_PAGE, data => {
      this.page = data;
      this.updatedUpdateUrl();
    });

    eventBus.$on(REFRESH_SEARCH, () => {
      this.searchTerm = '';
      this.updatedUpdateUrl();
    });
  },
  updated() {
    if (this.modalReady === false) {
      MicroModal.init();
    }
  },
  methods: {
    leftOptionsInterpolation() {
      return {
        mural:
          'Image can be used in single (48" x 96") or multi panel (up to 192" x 96")',
      };
    },
    // buildUrl will loop over a set of variables and concat
    buildUrl() {
      const {
        searchTerm,
        selectedCollection,
        page,
        selectedFilters: { color, scale },
        openImageId,
      } = this;

      const obj = {
        searchTerm,
        page: page.toString(),
        selectedCollection,
        filterColor: color,
        filterScale: scale,
        image: openImageId,
      };

      // create new query params based off of our App state
      return Object.keys(obj).reduce((acc, val) => {
        const regex = /\?/;
        const modeAware = newUrlCreator(Boolean(acc.match(regex)));
        acc = modeAware(acc, obj, val);
        return acc;
      }, '');
    },
    updatedUpdateUrl() {
      const stateObj = {
        imageSearch: 'image-gallery-portfolio',
      };

      history.pushState(stateObj, 'newUrl', this.buildUrl());
      this.makeSearchRequest();
    },
    // the open image is shareable state but not a history step
    replaceUrl() {
      const stateObj = {
        imageSearch: 'image-gallery-portfolio',
      };

      history.replaceState(stateObj, 'newUrl', this.buildUrl());
    },
    openImage(image) {
      this.activeImage = image;
      this.openImageId = imageIdOf(image);
      this.imageModalOpen = true;
      this.replaceUrl();
      this.$nextTick(() => {
        MicroModal.show('modal-libris-image', {
          onClose: () => this.handleImageModalClose(),
        });
      });
    },
    handleImageModalClose() {
      this.imageModalOpen = false;
      this.openImageId = '';
      this.replaceUrl();
    },
    // unknown or missing ids fall back to the plain grid
    async openImageFromUrl({ sharedLink = false } = {}) {
      const imageId = normalizeImageId(
        queryString.parse(location.search).image
      );
      if (!imageId) {
        if (this.imageModalOpen) this.$refs.librisModal.close();
        return;
      }
      if (this.imageModalOpen && imageId === this.openImageId) return;

      const image =
        this.results.find(result => imageIdOf(result) === imageId) ||
        (await this.fetchImage(imageId));
      if (image) {
        this.openImage(image);
        // Back/Forward reopens aren't shared-link visits
        if (sharedLink) this.sendAdobeSharedImageOpenEvent(imageId);
      } else {
        this.openImageId = '';
        this.replaceUrl();
      }
    },
    // the image may not be on the current results page
    fetchImage(imageId) {
      return axios
        .get('/proxy/media', {
          params: {
            terms: imageId,
            org_id,
            match_all_terms: false,
            galleries: gallery_id,
            per_page,
          },
        })
        .then(({ data }) =>
          (data.data || []).find(result => imageIdOf(result) === imageId)
        )
        .catch(err => {
          console.error('error fetching shared image', err);
        });
    },
    // The API call to fetch the categories -- if for whatever reason the category id has changed
    // you will have to update the constant in constants.js
    getCategories() {
      return axios
        .get(`/proxy/mem/custom_metadata/field/${LIBRIS_CATEGORY_ID}/options`)
        .then(({ data }) => {
          if (typeof data.error === 'undefined') {
            this.collectionOptions = data.data.FieldOption;
            this.error = null;
          } else {
            console.error('Successful error getting collections', data.error);
            this.error = data.error;
          }
        })
        .catch(err => {
          console.error('error getting collections', err);
          this.error = err;
        });
    },
    makeSearchRequest() {
      // since we're making a search request, we will be loading new data
      this.loadingData = true;
      // constuct custom_meatadata object to send;
      const filterMeta = Object.keys(this.selectedFilters).reduce(
        (acc, curVal) => {
          const currentKeyObject = this.selectedFilters[curVal];
          if (currentKeyObject && currentKeyObject.option) {
            acc[currentKeyObject.field_id] = currentKeyObject.option_id;
          }
          return acc;
        },
        {}
      );

      const collectionMeta =
        this.selectedCollection && this.selectedCollection.option_id
          ? {
              [this.selectedCollection.field_id]: this.selectedCollection
                .option_id,
            }
          : {};

      // First request to get the data on mount
      return axios
        .get('/proxy/media', {
          params: {
            terms: htmlEntities(withImageIdPrefix(this.searchTerm)),
            org_id,
            match_all_terms: false,
            galleries: gallery_id,
            custom_metadata: {
              ...filterMeta,
              ...collectionMeta,
            },
            per_page,
            page: this.page,
          },
        })
        .then(json => json.data)
        .then(json => {
          this.pageHeadingSearchTerm = this.searchTerm;
          this.totalPages = json.meta.paging.pages;
          this.results = json.data;
          this.totalResults = json.meta.paging.total;
          this.apiCallMade = true;
          this.error = null;
          this.loadingData = false;
        })
        .catch(err => {
          console.error(err);
          this.error = err;
        });
    },
    handleSearchTerms() {
      const parsed = queryString.parse(location.search);

      // loop over the query params that came from the URL
      for (let [key, val] of Object.entries(parsed)) {
        // first check to see if the key val pair is valid for our app
        if (this.isValidQueryString(val, key)) {
          // set state based on the valid query string
          if (
            key === queryKeys.QUERY_SEARCH_TERM ||
            key === queryKeys.QUERY_CURRENT_PAGE
          ) {
            this[key] = val;
          } else {
            // these options require us to do a mapping between app state
            const stateArray = this.getAppropriateStateArray(key);
            const newState = find(stateArray, ({ option }) => option == val);
            const [firstKey, secondKey] = queryToStateMap[key].split('.');

            if (newState) {
              if (!secondKey) {
                this[firstKey] = newState;
              } else {
                this[firstKey][secondKey] = newState;
              }
            }
          }
        }
      }
    },
    getAppropriateStateArray(queryStringKey) {
      if (queryStringKey === queryKeys.QUERY_SELECTED_COLLECTION) {
        return this.collectionOptions;
      }

      if (queryStringKey == queryKeys.QUERY_FILTER_COLOR) {
        return this.colorOptions;
      }

      if (queryStringKey === queryKeys.QUERY_FILTER_SCALE) {
        return this.scaleOptions;
      }
    },
    isValidQueryString(obj, key) {
      if (!obj) {
        return false;
      }

      // make sure the user passed in a string and that the key that was passed was a valid key
      return (
        typeof obj === 'string' &&
        Object.values(queryKeys).some(val => val == key)
      );
    },
    sendAdobeResultClickEvent(index) {
      const selectedImage = this.results[index];
      if (!selectedImage) return;

      const iptcAttributes =
        selectedImage.includes &&
        selectedImage.includes.iptc &&
        selectedImage.includes.iptc.data &&
        selectedImage.includes.iptc.data.attributes
          ? selectedImage.includes.iptc.data.attributes
          : {};
      const displayName =
        iptcAttributes.description || iptcAttributes.title || selectedImage.id;
      fireAdobeAnalyticsEvent({
        type: 'tool',
        detail: {
          eVar35: `Image Gallery|Select|${displayName}`,
        },
      });
    },
    sendAdobeModalOpenEvent(index) {
      const selectedImage = this.results[index];
      if (!selectedImage) return;

      const iptcAttributes =
        selectedImage.includes?.iptc?.data?.attributes || {};
      const rawName =
        iptcAttributes.description || iptcAttributes.title || selectedImage.id;
      const displayName = String(rawName || '')
        .replace('3FORM-', '')
        .trim();

      // Launch counts a search on a tool event as another search; opening a
      // result is a result click, reported with its position on the click.
      const search = this.adobeSearchObject();
      fireAdobeAnalyticsEvent({
        type: 'click',
        detail: {
          eVar8: `INP|Digital Print Library|Open Image ${displayName}`,
          ...(search && { search: { ...search, position: index + 1 } }),
        },
      });
    },
    sendAdobeSharedImageOpenEvent(imageId) {
      try {
        fireAdobeAnalyticsEvent({
          type: 'click',
          detail: {
            eVar8: `INP|Digital Print Library|Open Shared Image ${imageId}`,
          },
        });
      } catch (err) {
        console.error('Libris analytics error', err);
      }
    },
    adobeSearchObject() {
      if (!this.searchTerm) return null;

      return {
        term: this.searchTerm,
        results: !!this.results.length,
        toolName: 'Image Gallery',
      };
    },
    sendAdobeSearchEvent: debounce(function(term) {
      if (!term) return;
      const search = {
        term,
        results: !!this.results.length,
        toolName: 'Image Gallery',
      };
      // Launch counts every tool event as a tool action (event35), so a
      // search needs its own eVar35 value.
      fireAdobeAnalyticsEvent({
        type: 'tool',
        detail: { eVar35: `Image Gallery|Search|${term}`, search },
      });
    }, 500),
  },
};
</script>
